import { escapeHtml, sanitizeUrl } from "@profolio/security";
import type { ProfileData, SectionData, ColorPalette, PublishedNavigationItem } from "./types";

// ─── Scalable Inline SVG Micro-Icon Library ───────────────────────────────────

export type SvgIconName =
  | "calendar"
  | "university"
  | "institution"
  | "mapPin"
  | "map-pin"
  | "graduationCap"
  | "chalkboardTeacher"
  | "banknote"
  | "badge"
  | "save"
  | "disk"
  | "fileText"
  | "tag"
  | "users"
  | "user"
  | "building"
  | "mail"
  | "phone"
  | "globe"
  | "externalLink"
  | "download"
  | "chevronDown"
  | "chartBar"
  | "chart"
  | "check"
  | "book"
  | "code"
  | "linkedin"
  | "scholar"
  | "googlescholar"
  | "github"
  | "orcid"
  | "twitter"
  | "facebook"
  | "youtube"
  | "researchgate"
  | "close"
  | "x";

const CALENDAR_SVG = `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`;
const CLOSE_SVG = `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;
const UNIVERSITY_SVG = `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 21h18M3 10h18M5 10v11M19 10v11M9 10v11M15 10v11M12 2L2 7h20L12 2z"/></svg>`;
const MAPPIN_SVG = `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>`;
const USERS_SVG = `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`;
const SAVE_SVG = `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>`;
const CHART_SVG = `<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>`;
const FILETEXT_SVG = `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>`;
const CODE_SVG = `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>`;
const TAG_SVG = `<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>`;

const SVG_ICONS: Record<SvgIconName, string> = {
  calendar: CALENDAR_SVG,
  university: UNIVERSITY_SVG,
  institution: UNIVERSITY_SVG,
  mapPin: MAPPIN_SVG,
  "map-pin": MAPPIN_SVG,
  graduationCap: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c0 2 3 3 6 3s6-1 6-3v-5"/></svg>`,
  chalkboardTeacher: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 3h20v14H2z"/><path d="M8 21h8M12 17v4"/><circle cx="9" cy="9" r="2"/><path d="M6 15c0-1.5 1.5-2 3-2s3 .5 3 2M15 8h4M15 11h3"/></svg>`,
  banknote: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01M18 12h.01"/></svg>`,
  badge: TAG_SVG,
  save: SAVE_SVG,
  disk: SAVE_SVG,
  fileText: FILETEXT_SVG,
  book: FILETEXT_SVG,
  tag: TAG_SVG,
  users: USERS_SVG,
  user: USERS_SVG,
  code: CODE_SVG,
  building: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="9" y1="22" x2="9" y2="2"/><line x1="8" y1="6" x2="8.01" y2="6"/><line x1="16" y1="6" x2="16.01" y2="6"/><line x1="8" y1="10" x2="8.01" y2="10"/><line x1="16" y1="10" x2="16.01" y2="10"/><line x1="8" y1="14" x2="8.01" y2="14"/><line x1="16" y1="14" x2="16.01" y2="14"/></svg>`,
  mail: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>`,
  phone: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>`,
  globe: `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>`,
  externalLink: `<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>`,
  download: `<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`,
  chevronDown: `<svg class="chevron-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>`,
  chartBar: CHART_SVG,
  chart: CHART_SVG,
  check: `<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>`,
  linkedin: `<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 8.76a1.65 1.65 0 0 0 1.66-1.66 1.66 1.66 0 0 0-3.32 0c0 .92.74 1.66 1.66 1.66m1.39 9.74v-8.37H5.07v8.37h2.78z"/></svg>`,
  scholar: `<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M12 24a7 7 0 1 1 0-14 7 7 0 0 1 0 14zm0-24L0 9.5l4 3.18v6.82h3v-4.43l5 3.93 12-9.5z"/></svg>`,
  googlescholar: `<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M12 24a7 7 0 1 1 0-14 7 7 0 0 1 0 14zm0-24L0 9.5l4 3.18v6.82h3v-4.43l5 3.93 12-9.5z"/></svg>`,
  github: `<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.53 1.032 1.53 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0 1 12 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0 0 22 12.017C22 6.484 17.522 2 12 2z"/></svg>`,
  orcid: `<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zM7.369 4.378c.525 0 .947.431.947.947s-.422.947-.947.947a.95.95 0 0 1-.947-.947c0-.525.422-.947.947-.947zm-.722 3.038h1.444v10.041H6.647V7.416zm3.562 0h3.9c3.712 0 5.344 2.653 5.344 5.025 0 2.578-2.016 5.025-5.325 5.025h-3.919V7.416zm1.444 1.303v7.444h2.297c3.272 0 4.022-2.484 4.022-3.722 0-2.016-1.284-3.722-4.097-3.722h-2.222z"/></svg>`,
  twitter: `<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>`,
  facebook: `<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>`,
  youtube: `<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>`,
  researchgate: `<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M19.586 0c-.818 0-1.508.19-2.074.565-.563.377-.97.936-1.213 1.68a3.193 3.193 0 0 0-.112.437 8.365 8.365 0 0 0-4.144-1.077c-1.385 0-2.61.306-3.673.916-1.064.61-1.897 1.487-2.498 2.63C5.27 6.295 4.97 7.64 4.97 9.187c0 1.547.299 2.892.898 4.035.599 1.144 1.432 2.02 2.496 2.63 1.065.61 2.29.916 3.675.916 1.228 0 2.321-.242 3.28-.727.958-.485 1.708-1.156 2.25-2.015v1.934h2.247V4.025a3.1 3.1 0 0 0-.012-.27c.238-.348.55-.615.937-.8.388-.186.837-.28 1.348-.28.46 0 .867.098 1.22.293.353.196.623.473.81.83.188.358.281.785.281 1.282 0 .546-.11 1.002-.33 1.368-.22.366-.54.631-.96.795v2.09c.895-.213 1.62-.676 2.176-1.39.555-.713.833-1.638.833-2.774 0-.964-.206-1.792-.619-2.484-.412-.693-.974-1.22-1.686-1.583-.711-.362-1.527-.543-2.447-.543zM12.086 3.513c1.07 0 1.99.23 2.76.69.77.46 1.353 1.137 1.75 2.03.396.894.594 1.96.594 3.198 0 1.24-.198 2.304-.594 3.197-.397.894-.98 1.57-1.75 2.03-.77.46-1.69.69-2.76.69-1.07 0-1.99-.23-2.76-.69-.77-.46-1.353-1.136-1.75-2.03-.396-.893-.594-1.958-.594-3.197 0-1.238.198-2.304.594-3.198.397-.893.98-1.57 1.75-2.03.77-.46 1.69-.69 2.76-.69z"/></svg>`,
  close: CLOSE_SVG,
  x: CLOSE_SVG,
};

export function renderSvgIcon(name: string, extraClass = ""): string {
  const svg = SVG_ICONS[name as SvgIconName] || SVG_ICONS["fileText"];
  if (!svg) return "";
  const kebab = name.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
  const cls = `svg-icon svg-${kebab}${extraClass ? ` ${extraClass}` : ""}`;
  if (svg.includes('class="')) {
    return svg.replace('class="', `class="${cls} `);
  }
  return svg.replace("<svg", `<svg class="${cls}"`);
}

// ─── URL Display Formatter ───────────────────────────────────────────────────

export function cleanDisplayUrl(rawUrl: string): string {
  try {
    const trimmed = rawUrl.trim();
    const url = new URL(trimmed.startsWith("http") ? trimmed : `https://${trimmed}`);
    let host = url.hostname.replace(/^www\./, "");
    if (host.includes("workers.dev") || host.includes("profolio")) {
      const parts = url.pathname.split("/").filter(Boolean);
      if (parts.length > 0) {
        return `profolio.lk/${parts[parts.length - 1]}`;
      }
      return "profolio.lk";
    }
    const path = url.pathname === "/" ? "" : url.pathname;
    const display = `${host}${path}`.replace(/\/$/, "");
    return display.length > 45 ? `${display.slice(0, 42)}…` : display;
  } catch {
    return rawUrl.length > 45 ? `${rawUrl.slice(0, 42)}…` : rawUrl;
  }
}

// ─── Platform Icon & Label Resolver ──────────────────────────────────────────

export function resolvePlatformInfo(platform: string): { icon: SvgIconName; iconName: SvgIconName; label: string } {
  const norm = (platform || "").toLowerCase().trim();
  switch (norm) {
    case "linkedin":
      return { icon: "linkedin", iconName: "linkedin", label: "LinkedIn" };
    case "scholar":
      return { icon: "scholar", iconName: "scholar", label: "Google Scholar" };
    case "googlescholar":
    case "google_scholar":
      return { icon: "googlescholar", iconName: "googlescholar", label: "Google Scholar" };
    case "github":
      return { icon: "github", iconName: "github", label: "GitHub" };
    case "orcid":
      return { icon: "orcid", iconName: "orcid", label: "ORCID" };
    case "twitter":
    case "x":
      return { icon: "twitter", iconName: "twitter", label: "X (Twitter)" };
    case "facebook":
      return { icon: "facebook", iconName: "facebook", label: "Facebook" };
    case "youtube":
      return { icon: "youtube", iconName: "youtube", label: "YouTube" };
    case "researchgate":
      return { icon: "researchgate", iconName: "researchgate", label: "ResearchGate" };
    default:
      return { icon: "globe", iconName: "globe", label: platform ? platform.charAt(0).toUpperCase() + platform.slice(1) : "Website" };
  }
}

// ─── Shared Primitive 1: Skip to Content Link ────────────────────────────────

export function renderSkipLink(): string {
  return `<a href="#main-content" class="skip-to-content">Skip to main content</a>`;
}

// ─── Shared Primitive 2: Site Navigation with Adaptive "More" Dropdown ───────

export function renderSiteNavigation(
  profile: ProfileData,
  sections: SectionData[],
  _palette?: ColorPalette,
  options?: {
    maxPrimaryLinks?: number;
    navigation?: PublishedNavigationItem[];
    activeSlug?: string;
    basePath?: string;
  }
): string {
  const navItems = options?.navigation || profile.navigation;
  const activeSlug = options?.activeSlug !== undefined ? options?.activeSlug : (profile.activeSlug ?? "");
  const basePath = options?.basePath ?? profile.basePath ?? "";

  // ── 1. Multi-Page Navigation ────────────────────────────────────────────────
  if (navItems && navItems.length > 0) {
    const sortedNav = [...navItems].sort((a, b) => {
      if (a.isHome && !b.isHome) return -1;
      if (!a.isHome && b.isHome) return 1;
      return a.sortOrder - b.sortOrder;
    });

    if (sortedNav.length > 1) {
      const maxPrimary = options?.maxPrimaryLinks ?? 5;
      const primaryPages = sortedNav.slice(0, maxPrimary);
      const secondaryPages = sortedNav.slice(maxPrimary);

      const getPageHref = (slug: string, isHome: boolean): string => {
        if (isHome || !slug) {
          return basePath ? `${basePath}/` : "/";
        }
        return basePath ? `${basePath}/${slug}` : `/${slug}`;
      };

      const isPageActive = (slug: string, isHome: boolean): boolean => {
        if (isHome || !slug) {
          return !activeSlug || activeSlug === "" || activeSlug === "/";
        }
        return activeSlug.toLowerCase() === slug.toLowerCase();
      };

      const primaryLinksHtml = primaryPages
        .map((p) => {
          const href = getPageHref(p.slug, p.isHome);
          const active = isPageActive(p.slug, p.isHome);
          const activeClass = active ? " active" : "";
          const ariaCurrent = active ? ' aria-current="page"' : "";
          return `<a href="${href}" class="nav-link${activeClass}" data-nav-slug="${p.slug}"${ariaCurrent}>${escapeHtml(p.label)}</a>`;
        })
        .join("\n");

      let moreDropdownHtml = "";
      if (secondaryPages.length > 0) {
        const dropdownItems = secondaryPages
          .map((p) => {
            const href = getPageHref(p.slug, p.isHome);
            const active = isPageActive(p.slug, p.isHome);
            const activeClass = active ? " active" : "";
            const ariaCurrent = active ? ' aria-current="page"' : "";
            return `<a href="${href}" class="dropdown-link${activeClass}" data-nav-slug="${p.slug}"${ariaCurrent}>${escapeHtml(p.label)}</a>`;
          })
          .join("\n");

        moreDropdownHtml = `
          <div class="nav-dropdown-wrap">
            <button type="button" class="nav-pill nav-dropdown-btn" aria-expanded="false" aria-haspopup="true">
              <span>More</span>
              ${renderSvgIcon("chevronDown", "dropdown-chevron")}
            </button>
            <div class="nav-dropdown-menu" role="menu">
              ${dropdownItems}
            </div>
          </div>`;
      }

      // Mobile drawer links includes all visible pages in same sort order
      const mobileDrawerLinks = sortedNav
        .map((p) => {
          const href = getPageHref(p.slug, p.isHome);
          const active = isPageActive(p.slug, p.isHome);
          const activeClass = active ? " active" : "";
          const ariaCurrent = active ? ' aria-current="page"' : "";
          return `<a href="${href}" class="mobile-nav-link${activeClass}" data-nav-slug="${p.slug}"${ariaCurrent}>${escapeHtml(p.label)}</a>`;
        })
        .join("\n");

      const brandHref = getPageHref("", true);
      const fullName = escapeHtml(profile.fullName || "Portfolio");

      return `
        <nav class="sticky-nav" aria-label="Main Navigation">
          <div class="nav-container">
            <a href="${brandHref}" class="nav-brand">${fullName}</a>
            <div class="nav-links-desktop">
              ${primaryLinksHtml}
              ${moreDropdownHtml}
            </div>
            <button type="button" class="mobile-menu-toggle" aria-label="Toggle navigation menu" aria-expanded="false">
              <span class="hamburger-bar"></span>
              <span class="hamburger-bar"></span>
              <span class="hamburger-bar"></span>
            </button>
          </div>
          <div class="mobile-nav-drawer" aria-hidden="true">
            <div class="mobile-nav-links">
              ${mobileDrawerLinks}
            </div>
          </div>
        </nav>`;
    }
  }

  // ── 2. Single-Page Sections Hash Navigation Fallback ────────────────────────
  const visibleSections = sections.filter((s) => s.visible).sort((a, b) => a.displayOrder - b.displayOrder);
  if (visibleSections.length <= 1) return "";

  const maxPrimary = options?.maxPrimaryLinks ?? 5;
  const primarySections = visibleSections.slice(0, maxPrimary);
  const secondarySections = visibleSections.slice(maxPrimary);

  const getSectionLabel = (type: string): string => {
    switch (type) {
      case "about":
        return "About";
      case "publications":
        return "Publications";
      case "research":
        return "Research";
      case "experience":
        return "Experience";
      case "teaching":
        return "Teaching";
      case "projects":
        return "Projects";
      case "skills":
        return "Skills";
      case "downloads":
        return "Resources";
      case "contact":
        return "Contact";
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  const primaryLinksHtml = primarySections
    .map((s) => {
      const label = getSectionLabel(s.type);
      return `<a href="#${s.type}" class="nav-link">${escapeHtml(label)}</a>`;
    })
    .join("\n");

  let moreDropdownHtml = "";
  if (secondarySections.length > 0) {
    const dropdownItems = secondarySections
      .map((s) => {
        const label = getSectionLabel(s.type);
        return `<a href="#${s.type}" class="dropdown-link" data-nav-section="${s.type}">${escapeHtml(label)}</a>`;
      })
      .join("\n");

    moreDropdownHtml = `
      <div class="nav-dropdown-wrap">
        <button type="button" class="nav-pill nav-dropdown-btn" aria-expanded="false" aria-haspopup="true">
          <span>More</span>
          ${renderSvgIcon("chevronDown", "dropdown-chevron")}
        </button>
        <div class="nav-dropdown-menu" role="menu">
          ${dropdownItems}
        </div>
      </div>`;
  }

  // Mobile drawer links includes all visible sections
  const mobileDrawerLinks = visibleSections
    .map((s) => {
      const label = getSectionLabel(s.type);
      return `<a href="#${s.type}" class="mobile-nav-link" data-nav-section="${s.type}">${escapeHtml(label)}</a>`;
    })
    .join("\n");

  const fullName = escapeHtml(profile.fullName || "Portfolio");

  return `
    <nav class="sticky-nav" aria-label="Main Navigation">
      <div class="nav-container">
        <a href="#" class="nav-brand">${fullName}</a>
        <div class="nav-links-desktop">
          ${primaryLinksHtml}
          ${moreDropdownHtml}
        </div>
        <button type="button" class="mobile-menu-toggle" aria-label="Toggle navigation menu" aria-expanded="false">
          <span class="hamburger-bar"></span>
          <span class="hamburger-bar"></span>
          <span class="hamburger-bar"></span>
        </button>
      </div>
      <div class="mobile-nav-drawer" aria-hidden="true">
        <div class="mobile-nav-links">
          ${mobileDrawerLinks}
        </div>
      </div>
    </nav>`;
}

// ─── Shared Primitive 3: Hero Identity ───────────────────────────────────────

export function renderHeroIdentity(
  profile: ProfileData,
  options?: {
    layout?: "side-by-side" | "centered" | "minimal";
    customCtaHtml?: string;
  }
): string {
  const fullName = escapeHtml(profile.fullName || "Scholar");
  const title = profile.title ? escapeHtml(profile.title) : "";
  const bio = profile.bio ? escapeHtml(profile.bio) : "";
  const photoUrl = profile.photoUrl ? sanitizeUrl(profile.photoUrl) : "";
  const layout = options?.layout || "side-by-side";

  // Avatar block
  const avatarHtml = photoUrl
    ? `
      <div class="hero-avatar-frame">
        <img src="${escapeHtml(photoUrl)}" alt="${fullName}" class="hero-avatar-img" loading="eager" />
      </div>`
    : `
      <div class="hero-avatar-frame hero-avatar-monogram">
        <span>${escapeHtml(fullName.charAt(0) || "P")}</span>
      </div>`;

  // Action Buttons Group
  const defaultCtaHtml = `
    <div class="hero-actions">
      <a href="#contact" class="btn-action">
        ${renderSvgIcon("mail")}
        <span>Get in Touch</span>
      </a>
      <a href="#downloads" class="btn-outline">
        ${renderSvgIcon("download")}
        <span>View Resources</span>
      </a>
    </div>`;

  const actionsHtml = options?.customCtaHtml || defaultCtaHtml;

  if (layout === "centered") {
    return `
      <header class="hero-identity hero-centered">
        ${avatarHtml}
        <div class="hero-content">
          <h1 class="hero-name">${fullName}</h1>
          ${title ? `<p class="hero-title">${title}</p>` : ""}
          ${bio ? `<p class="hero-bio">${bio}</p>` : ""}
          ${actionsHtml}
        </div>
      </header>`;
  }

  return `
    <header class="hero-identity hero-split">
      <div class="hero-content">
        <h1 class="hero-name">${fullName}</h1>
        ${title ? `<div class="hero-title-badge">${title}</div>` : ""}
        ${bio ? `<p class="hero-bio">${bio}</p>` : ""}
        ${actionsHtml}
      </div>
      <div class="hero-avatar-col">
        ${avatarHtml}
      </div>
    </header>`;
}

// ─── Shared Primitive 4: Section Header ───────────────────────────────────────

export function renderSectionHeader(
  title: string,
  sectionType: string,
  options?: { eyebrow?: string; subtitle?: string }
): string {
  const safeTitle = escapeHtml(title);
  const eyebrow = options?.eyebrow ? `<span class="section-eyebrow">${escapeHtml(options.eyebrow)}</span>` : "";
  const subtitle = options?.subtitle ? `<p class="section-subtitle">${escapeHtml(options.subtitle)}</p>` : "";

  return `
    <div class="section-header-block">
      ${eyebrow}
      <h2 class="section-title" id="title-${sectionType}">${safeTitle}</h2>
      ${subtitle}
    </div>`;
}

export interface FilterOption {
  id?: string;
  key?: string;
  label: string;
  count?: number;
}

export function renderFilterBar(
  arg1: string | FilterOption[],
  arg2: string | FilterOption[],
  activeKey = "all"
): string {
  let targetSelector = "";
  let options: FilterOption[] = [];

  if (Array.isArray(arg1)) {
    options = arg1;
    targetSelector = typeof arg2 === "string" ? arg2 : "";
  } else if (Array.isArray(arg2)) {
    options = arg2;
    targetSelector = typeof arg1 === "string" ? arg1 : "";
  }

  if (!options || options.length < 2) return "";

  const desktopButtons = options
    .map((opt) => {
      const k = opt.id || opt.key || "all";
      const isActive = k === activeKey ? " active" : "";
      const countBadge = typeof opt.count === "number" ? `<span class="filter-count">(${opt.count})</span>` : "";
      return `<button type="button" class="filter-btn filter-tab-btn${isActive}" data-filter="${escapeHtml(k)}" data-filter-key="${escapeHtml(k)}" aria-pressed="${k === activeKey ? "true" : "false"}">${escapeHtml(opt.label)} ${countBadge}</button>`;
    })
    .join("\n");

  const selectOptions = options
    .map((opt) => {
      const k = opt.id || opt.key || "all";
      const isSelected = k === activeKey ? " selected" : "";
      const countText = typeof opt.count === "number" ? ` (${opt.count})` : "";
      return `<option value="${escapeHtml(k)}"${isSelected}>${escapeHtml(opt.label)}${countText}</option>`;
    })
    .join("\n");

  const safeTarget = escapeHtml(targetSelector);

  return `
    <div class="filter-bar-container" data-filter-bar data-filter-target="${safeTarget}" data-filter-group="${safeTarget}">
      <div class="filter-tabs-desktop" role="tablist">
        ${desktopButtons}
      </div>
      <div class="filter-select-mobile">
        <select class="filter-select filter-dropdown" data-filter-target="${safeTarget}" aria-label="Filter items">
          ${selectOptions}
        </select>
      </div>
    </div>`;
}

// ─── Shared Primitive 6: Disclosure Panel ─────────────────────────────────────

export function renderDisclosurePanel(
  summaryLabel: string,
  bodyHtml: string,
  options?: { detailsClass?: string }
): string {
  if (!bodyHtml) return "";
  const cls = options?.detailsClass ? ` ${options.detailsClass}` : "";

  return `
    <details class="disclosure-panel${cls}">
      <summary class="disclosure-toggle">
        <span class="disclosure-label">${escapeHtml(summaryLabel)}</span>
        ${renderSvgIcon("chevronDown", "disclosure-chevron")}
      </summary>
      <div class="disclosure-content">
        ${bodyHtml}
      </div>
    </details>`;
}

// ─── Shared Primitive 7: Timeline Entry ───────────────────────────────────────

export interface TimelineEntryInput {
  role: string;
  organization?: string;
  department?: string;
  location?: string;
  period: string;
  isCurrent?: boolean;
  descriptionHtml?: string;
}

export function renderTimelineEntry(item: TimelineEntryInput): string {
  const role = escapeHtml(item.role);
  const org = item.organization ? escapeHtml(item.organization) : "";
  const dept = item.department ? escapeHtml(item.department) : "";
  const loc = item.location ? escapeHtml(item.location) : "";
  const period = escapeHtml(item.period);

  const chips: string[] = [];
  if (period) {
    chips.push(`<span class="meta-chip">${renderSvgIcon("calendar")} ${period}</span>`);
  }
  if (dept) {
    chips.push(`<span class="meta-chip">${renderSvgIcon("university")} ${dept}</span>`);
  }
  if (loc) {
    chips.push(`<span class="meta-chip">${renderSvgIcon("mapPin")} ${loc}</span>`);
  }

  return `
    <div class="timeline-entry">
      <div class="timeline-marker" aria-hidden="true">
        <div class="timeline-node"></div>
      </div>
      <div class="timeline-card">
        <div class="timeline-header">
          <div class="timeline-heading-group">
            <h3 class="timeline-role">${role}</h3>
            ${org ? `<span class="timeline-org">${org}</span>` : ""}
          </div>
          ${item.isCurrent ? `<span class="badge-status active">Current</span>` : ""}
        </div>
        ${chips.length > 0 ? `<div class="timeline-chips">${chips.join(" ")}</div>` : ""}
        ${item.descriptionHtml ? `<div class="timeline-desc">${item.descriptionHtml}</div>` : ""}
      </div>
    </div>`;
}

// ─── Shared Primitive 8: Contact Card Matrix ──────────────────────────────────

export interface ContactChannel {
  type: "email" | "phone" | "office" | "address" | "website";
  label: string;
  value: string;
  href?: string;
}

export function renderContactMatrix(
  channels: ContactChannel[],
  socialLinks: Array<{ platform: string; url: string; label?: string }>
): string {
  const channelCardsHtml = channels
    .map((c) => {
      let iconName: SvgIconName = "globe";
      if (c.type === "email") iconName = "mail";
      else if (c.type === "phone") iconName = "phone";
      else if (c.type === "office") iconName = "building";
      else if (c.type === "address") iconName = "mapPin";

      let valHtml = escapeHtml(c.value);
      if (c.href) {
        valHtml = `<a href="${c.href}" ${c.href.startsWith("http") ? 'target="_blank" rel="noopener noreferrer"' : ""}>${escapeHtml(c.value)}</a>`;
      }

      return `
        <div class="contact-card">
          <div class="contact-card-icon" aria-hidden="true">
            ${renderSvgIcon(iconName)}
          </div>
          <div class="contact-card-body">
            <span class="contact-card-label">${escapeHtml(c.label)}</span>
            <span class="contact-card-val">${valHtml}</span>
          </div>
        </div>`;
    })
    .join("\n");

  let socialPillsHtml = "";
  if (socialLinks.length > 0) {
    const pills = socialLinks
      .map((s) => {
        const safeUrl = sanitizeUrl(s.url);
        if (!safeUrl) return "";
        const info = resolvePlatformInfo(s.platform);
        const label = s.label ? escapeHtml(s.label) : info.label;

        return `
          <a href="${safeUrl}" target="_blank" rel="noopener noreferrer" class="social-pill" aria-label="${label}">
            ${renderSvgIcon(info.icon, "social-icon")}
            <span>${label}</span>
            ${renderSvgIcon("externalLink", "external-arrow")}
          </a>`;
      })
      .filter(Boolean)
      .join("\n");

    if (pills) {
      socialPillsHtml = `
        <div class="contact-social-section">
          <h3 class="contact-social-heading">Profiles &amp; Networks</h3>
          <div class="social-pills-row">
            ${pills}
          </div>
        </div>`;
    }
  }

  return `
    <div class="contact-matrix-grid">
      ${channelCardsHtml}
    </div>
    ${socialPillsHtml}`;
}

// ─── Shared Primitive 9: Accessible Semantic Detail Dialog ─────────────────────

export interface DetailDialogOptions {
  id: string;
  title: string;
  subtitle?: string;
  categoryBadge?: string;
  bodyHtml: string;
  actionLinksHtml?: string;
}

export function renderDetailDialog(options: DetailDialogOptions): string {
  const safeId = (options.id || "item").replace(/[^a-zA-Z0-9_-]/g, "");
  const safeTitle = escapeHtml(options.title || "");
  const subtitleHtml = options.subtitle
    ? `<div class="profolio-dialog-subtitle">${escapeHtml(options.subtitle)}</div>`
    : "";
  const categoryHtml = options.categoryBadge
    ? `<span class="profolio-dialog-badge">${escapeHtml(options.categoryBadge)}</span>`
    : "";
  const actionsHtml = options.actionLinksHtml
    ? `<div class="profolio-dialog-footer">${options.actionLinksHtml}</div>`
    : "";

  return `
    <dialog id="dialog-${safeId}" class="profolio-dialog" data-profolio-dialog aria-labelledby="dialog-title-${safeId}">
      <div class="profolio-dialog-surface">
        <div class="profolio-dialog-header">
          <div class="profolio-dialog-heading-wrap">
            ${categoryHtml}
            <h3 id="dialog-title-${safeId}" class="profolio-dialog-title">${safeTitle}</h3>
            ${subtitleHtml}
          </div>
          <button type="button" class="profolio-dialog-close-btn" data-dialog-close aria-label="Close dialog">
            ${renderSvgIcon("close")}
          </button>
        </div>
        <div class="profolio-dialog-body">
          ${options.bodyHtml}
        </div>
        ${actionsHtml}
      </div>
    </dialog>
  `;
}

